import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { ExternalLink, Info, ArrowUpDown, ArrowUp, ArrowDown, Globe, Newspaper } from 'lucide-react';
import { translateCategories } from '../../utils/translations';
import { useLanguage } from '../../context/LanguageContext';
import { ExampleLinkIcon } from './ExampleLinkIcon';

// Country to flag - using flag CDN images
const getCountryFlag = (country) => {
  if (!country) return null;
  const countryUpper = country.toUpperCase().trim();
  const countryCodes = {
    'SPAIN': 'es', 'SPAIN (SPANISH)': 'es', 'ESPAÑA': 'es',
    'FRANCE': 'fr', 'FRANCE (FRENCH)': 'fr', 'FRANCIA': 'fr',
    'GERMANY': 'de', 'GERMANY (GERMAN)': 'de', 'ALEMANIA': 'de',
    'ITALY': 'it', 'ITALY (ITALIAN)': 'it', 'ITALIA': 'it',
    'PORTUGAL': 'pt', 'PORTUGAL (PORTUGUESE)': 'pt',
    'UK': 'gb', 'UNITED KINGDOM': 'gb', 'REINO UNIDO': 'gb', 'GB': 'gb', 'GREAT BRITAIN': 'gb',
    'USA': 'us', 'UNITED STATES': 'us', 'ESTADOS UNIDOS': 'us', 'US': 'us',
    'NETHERLANDS': 'nl', 'HOLANDA': 'nl', 'PAISES BAJOS': 'nl',
    'BELGIUM': 'be', 'BÉLGICA': 'be', 'BELGICA': 'be',
    'AUSTRIA': 'at',
    'SWITZERLAND': 'ch', 'SUIZA': 'ch',
    'POLAND': 'pl', 'POLONIA': 'pl',
    'CZECH REPUBLIC': 'cz', 'CZECHIA': 'cz', 'REPÚBLICA CHECA': 'cz',
    'SWEDEN': 'se', 'SUECIA': 'se',
    'NORWAY': 'no', 'NORUEGA': 'no',
    'DENMARK': 'dk', 'DINAMARCA': 'dk',
    'FINLAND': 'fi', 'FINLANDIA': 'fi',
    'IRELAND': 'ie', 'IRLANDA': 'ie',
    'GREECE': 'gr', 'GRECIA': 'gr',
    'ROMANIA': 'ro', 'RUMANIA': 'ro',
    'HUNGARY': 'hu', 'HUNGRÍA': 'hu', 'HUNGRIA': 'hu',
    'BULGARIA': 'bg',
    'CROATIA': 'hr', 'CROACIA': 'hr',
    'SLOVAKIA': 'sk', 'ESLOVAQUIA': 'sk',
    'SLOVENIA': 'si', 'ESLOVENIA': 'si',
    'LITHUANIA': 'lt', 'LITUANIA': 'lt',
    'LATVIA': 'lv', 'LETONIA': 'lv',
    'ESTONIA': 'ee',
    'CYPRUS': 'cy', 'CHIPRE': 'cy',
    'MALTA': 'mt',
    'LUXEMBOURG': 'lu', 'LUXEMBURGO': 'lu',
    'BRAZIL': 'br', 'BRASIL': 'br',
    'MEXICO': 'mx', 'MÉXICO': 'mx',
    'ARGENTINA': 'ar',
    'CHILE': 'cl',
    'COLOMBIA': 'co',
    'PERU': 'pe', 'PERÚ': 'pe',
    'INDIA': 'in',
    'CHINA': 'cn',
    'JAPAN': 'jp', 'JAPÓN': 'jp', 'JAPON': 'jp',
    'SOUTH KOREA': 'kr', 'COREA DEL SUR': 'kr',
    'AUSTRALIA': 'au',
    'CANADA': 'ca', 'CANADÁ': 'ca',
    'RUSSIA': 'ru', 'RUSIA': 'ru',
    'TURKEY': 'tr', 'TURQUÍA': 'tr', 'TURQUIA': 'tr',
    'INDONESIA': 'id',
    'THAILAND': 'th', 'TAILANDIA': 'th',
    'VIETNAM': 'vn',
    'MALAYSIA': 'my', 'MALASIA': 'my',
    'SINGAPORE': 'sg', 'SINGAPUR': 'sg',
    'PHILIPPINES': 'ph', 'FILIPINAS': 'ph',
    'EGYPT': 'eg', 'EGIPTO': 'eg',
    'SOUTH AFRICA': 'za', 'SUDÁFRICA': 'za', 'SUDAFRICA': 'za',
    'ISRAEL': 'il',
    'UNITED ARAB EMIRATES': 'ae', 'UAE': 'ae', 'EMIRATOS ÁRABES': 'ae',
    'SAUDI ARABIA': 'sa', 'ARABIA SAUDITA': 'sa',
    'UKRAINE': 'ua', 'UCRANIA': 'ua',
    'GLOBAL': null,
    'INTERNATIONAL': null,
  };
  const code = countryCodes[countryUpper];
  return code ? `https://flagcdn.com/24x18/${code}.png` : null;
};

// Parse ALL link types from the field and return array of badges
const getLinkTypeBadges = (linkType) => {
  if (!linkType || linkType === 'No') return [];
  const badges = [];
  const lower = linkType.toLowerCase();
  const parts = lower.split(',').map(p => p.trim());

  for (const part of parts) {
    // Check for follow/dofollow (but not no-follow/nofollow)
    if ((part === 'follow' || part.includes('dofollow')) && !part.includes('no-follow') && !part.includes('nofollow')) {
      badges.push({ label: 'DoFollow', color: 'bg-blue-100 text-blue-700' });
    }
    // Check for no-follow/nofollow
    if (part.includes('no-follow') || part.includes('nofollow')) {
      badges.push({ label: 'NoFollow', color: 'bg-slate-100 text-slate-600' });
    }
    if (part.includes('sponsor')) {
      badges.push({ label: 'Sponsored', color: 'bg-amber-100 text-amber-700' });
    }
    if (part.includes('ugc')) {
      badges.push({ label: 'UGC', color: 'bg-purple-100 text-purple-700' });
    }
  }

  return badges;
};

const SENSITIVE_KEYS = ['casino', 'cbd', 'gambling', 'cryptocurrency', 'sex', 'erotic', 'vpn', 'tarot', 'porn', 'cannabis'];
const seoVal = (p, k) => (p.seo_metrics && p.seo_metrics[k] !== null && p.seo_metrics[k] !== undefined ? p.seo_metrics[k] : null);
const HeadTip = ({ tip, children }) => (
  <Tooltip>
    <TooltipTrigger asChild><span className="cursor-help border-b border-dotted border-current">{children}</span></TooltipTrigger>
    <TooltipContent side="top" className="max-w-[220px] text-xs normal-case font-normal">{tip}</TooltipContent>
  </Tooltip>
);
const Dash = () => <span className="text-slate-300 text-[10px]">—</span>;

export const ProductTable = ({ products, texts, onProductClick, onSortChange, currentSortField, currentSortOrder }) => {
  const { language } = useLanguage();
  
  // Use props for sort state if provided (server-side sorting), else local state (client-side)
  const [localSortField, setLocalSortField] = useState('');
  const [localSortOrder, setLocalSortOrder] = useState('desc');
  
  const sortField = currentSortField !== undefined ? currentSortField : localSortField;
  const sortOrder = currentSortOrder !== undefined ? currentSortOrder : localSortOrder;

  const t = texts || {
    tableUrl: 'URL',
    tableCountry: 'País',
    tablePrice: 'Precio',
    tablePriceSensitive: 'Precio Sensible',
    tableDr: 'DR',
    tableTraffic: 'Tráfico',
    tableSiteType: 'Tipo Medio',
    tableLinkType: 'Tipo Enlace',
    tableCategory: 'Categoría',
    tableMoreInfo: 'Info',
  };

  // Handle table sort - notifies parent for server-side sorting
  const handleSort = (field) => {
    let newOrder;
    if (sortField === field) {
      newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      newOrder = 'desc';
    }
    
    // If parent provided onSortChange, use server-side sorting
    if (onSortChange) {
      onSortChange(field, newOrder);
    } else {
      // Fallback to local state for client-side sorting
      setLocalSortField(field);
      setLocalSortOrder(newOrder);
    }
  };

  // Get sorted products - only used for client-side sorting (when onSortChange not provided)
  const getSortedProducts = () => {
    // If using server-side sorting, products are already sorted
    if (onSortChange) return products;
    
    if (!sortField) return products;

    return [...products].sort((a, b) => {
      let aVal, bVal;
      
      if (sortField === 'price' || sortField === 'pvp') {
        aVal = a.pvp || a.price || 0;
        bVal = b.pvp || b.price || 0;
      } else if (sortField === 'dr') {
        aVal = a.dr || 0;
        bVal = b.dr || 0;
      } else if (sortField === 'traffic' || sortField === 'organic_traffic') {
        aVal = a.traffic || a.organic_traffic || 0;
        bVal = b.traffic || b.organic_traffic || 0;
      } else {
        aVal = a[sortField] || 0;
        bVal = b[sortField] || 0;
      }
      
      if (sortOrder === 'asc') {
        return aVal - bVal;
      }
      return bVal - aVal;
    });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-md" data-testid="product-table" style={{maxHeight: '70vh', overflow: 'auto'}}>
      <TooltipProvider delayDuration={100}>
      <table className="w-full text-xs border-collapse">
        <thead className="sticky top-0 z-30 bg-indigo-100" style={{position: 'sticky', top: 0}}>
          <tr className="border-b-2 border-indigo-300">
            <th className="font-bold text-indigo-900 whitespace-nowrap px-1.5 py-3 text-left bg-indigo-100">{t.tableUrl}</th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-1.5 py-3 text-left hidden sm:table-cell bg-indigo-100">{t.tableCountry}</th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-1.5 py-3 text-left cursor-pointer hover:bg-indigo-200 bg-indigo-100" onClick={() => handleSort('price')}>
              <div className="flex items-center gap-1">
                {t.tablePrice}
                {sortField === 'price' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-amber-700 whitespace-nowrap px-1.5 py-3 text-left hidden md:table-cell bg-indigo-100"><HeadTip tip={language === 'es' ? 'Precio para contenidos de temáticas sensibles (casino, CBD, cripto…)' : 'Price for sensitive-topic content (casino, CBD, crypto…)'}>{t.tablePriceSensitive}</HeadTip></th>
            <th className="font-semibold text-indigo-700 whitespace-nowrap px-1 py-3 text-left hidden min-[1680px]:table-cell bg-indigo-100 text-[11px] cursor-pointer hover:bg-indigo-200" onClick={() => handleSort('pvp_insertion')} title={language === 'es' ? 'PVP Inserción normal' : 'Insertion PVP normal'}>
              <div className="flex items-center gap-1">
                <HeadTip tip={language === 'es' ? 'Precio de inserción de enlace en un artículo ya publicado' : 'Price to insert a link in an already published article'}>{language === 'es' ? 'Inserc.' : 'Insert.'}</HeadTip>
                {sortField === 'pvp_insertion' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-indigo-700 whitespace-nowrap px-1 py-3 text-left hidden min-[1680px]:table-cell bg-indigo-100 text-[11px] cursor-pointer hover:bg-indigo-200" onClick={() => handleSort('pvp_insertion_sensitive')} title={language === 'es' ? 'PVP Inserción sensible' : 'Insertion PVP sensitive'}>
              <div className="flex items-center gap-1">
                <HeadTip tip={language === 'es' ? 'Precio de inserción para temáticas sensibles' : 'Insertion price for sensitive topics'}>{language === 'es' ? 'Inserc. S.' : 'Insert. S.'}</HeadTip>
                {sortField === 'pvp_insertion_sensitive' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-rose-700 whitespace-nowrap px-1 py-3 text-left hidden min-[1680px]:table-cell bg-indigo-100 text-[11px] cursor-pointer hover:bg-indigo-200" onClick={() => handleSort('pvp_mention')} title={language === 'es' ? 'PVP Mención normal' : 'Mention PVP normal'}>
              <div className="flex items-center gap-1">
                <HeadTip tip={language === 'es' ? 'Precio de mención de marca (sin enlace)' : 'Brand mention price (no link)'}>{language === 'es' ? 'Menc.' : 'Ment.'}</HeadTip>
                {sortField === 'pvp_mention' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-rose-700 whitespace-nowrap px-1 py-3 text-left hidden min-[1680px]:table-cell bg-indigo-100 text-[11px] cursor-pointer hover:bg-indigo-200" onClick={() => handleSort('pvp_mention_sensitive')} title={language === 'es' ? 'PVP Mención sensible' : 'Mention PVP sensitive'}>
              <div className="flex items-center gap-1">
                <HeadTip tip={language === 'es' ? 'Precio de mención para temáticas sensibles' : 'Mention price for sensitive topics'}>{language === 'es' ? 'Menc. S.' : 'Ment. S.'}</HeadTip>
                {sortField === 'pvp_mention_sensitive' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-1.5 py-3 text-left cursor-pointer hover:bg-indigo-200 bg-indigo-100" onClick={() => handleSort('dr')}>
              <div className="flex items-center gap-1">
                <HeadTip tip={language === 'es' ? 'Domain Rating (Ahrefs): autoridad del dominio de 0 a 100' : 'Domain Rating (Ahrefs): domain authority 0–100'}>{t.tableDr}</HeadTip>
                {sortField === 'dr' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-1.5 py-3 text-left cursor-pointer hover:bg-indigo-200 bg-indigo-100" onClick={() => handleSort('traffic')}>
              <div className="flex items-center gap-1">
                <HeadTip tip={language === 'es' ? 'Visitas orgánicas mensuales estimadas' : 'Estimated monthly organic visits'}>{t.tableTraffic}</HeadTip>
                {sortField === 'traffic' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-orange-600 whitespace-nowrap px-1.5 py-3 text-left hidden lg:table-cell cursor-pointer hover:bg-indigo-200 bg-indigo-100" onClick={() => handleSort('tf')} data-testid="sort-tf">
              <div className="flex items-center gap-1">
                <HeadTip tip={language === 'es' ? 'Trust Flow (Majestic): calidad y confianza de los enlaces que recibe' : 'Trust Flow (Majestic): quality and trust of incoming links'}>TF</HeadTip>
                {sortField === 'tf' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-rose-600 whitespace-nowrap px-1.5 py-3 text-left hidden lg:table-cell cursor-pointer hover:bg-indigo-200 bg-indigo-100" onClick={() => handleSort('rd')} data-testid="sort-rd">
              <div className="flex items-center gap-1">
                <HeadTip tip={language === 'es' ? 'Referring Domains: dominios distintos que enlazan al sitio' : 'Referring Domains: unique domains linking to the site'}>RD</HeadTip>
                {sortField === 'rd' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-red-600 whitespace-nowrap px-1.5 py-3 text-left hidden lg:table-cell cursor-pointer hover:bg-indigo-200 bg-indigo-100" onClick={() => handleSort('keywords')} data-testid="sort-keywords">
              <div className="flex items-center gap-1">
                <HeadTip tip={language === 'es' ? 'Keywords: palabras clave por las que posiciona en Google' : 'Keywords: terms the site ranks for on Google'}>KW</HeadTip>
                {sortField === 'keywords' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-1.5 py-3 text-left hidden lg:table-cell cursor-pointer hover:bg-indigo-200 bg-indigo-100" onClick={() => handleSort('gg_news')} data-testid="sort-gg_news">
              <div className="flex items-center gap-1">
                <HeadTip tip={language === 'es' ? 'Google News: el medio aparece en Google News' : 'Google News: the site is listed in Google News'}>GN</HeadTip>
                {sortField === 'gg_news' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-1.5 py-3 text-left hidden lg:table-cell bg-indigo-100">{t.tableSiteType}</th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-1.5 py-3 text-left hidden xl:table-cell bg-indigo-100">{t.tableLinkType}</th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-1.5 py-3 text-left hidden xl:table-cell bg-indigo-100"><HeadTip tip={language === 'es' ? 'Tiempo que el enlace permanece publicado' : 'How long the link stays published'}>{language === 'es' ? 'Duración' : 'Duration'}</HeadTip></th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-1.5 py-3 text-center hidden xl:table-cell bg-indigo-100"><HeadTip tip={language === 'es' ? 'Acepta temáticas sensibles' : 'Accepts sensitive topics'}>Sens.</HeadTip></th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-1.5 py-3 text-center hidden xl:table-cell bg-indigo-100"><HeadTip tip={language === 'es' ? 'Indica si el artículo se marca como patrocinado/publicidad' : 'Whether the article is labelled as sponsored/ad'}>Tag</HeadTip></th>
            <th className="text-center font-semibold text-slate-700 whitespace-nowrap px-1.5 py-3 bg-indigo-100"><HeadTip tip={language === 'es' ? 'Ejemplo de un artículo ya publicado en el medio' : 'Example of an article already published on the site'}>{language === 'es' ? 'Ejemplo' : 'Example'}</HeadTip></th>
            <th className="text-center font-semibold text-slate-700 whitespace-nowrap px-1.5 py-3 bg-indigo-100 sticky right-0 z-10">{t.tableMoreInfo}</th>
          </tr>
        </thead>
        <tbody>
          {getSortedProducts().map((product, index) => (
            <tr 
              key={product.id} 
              className={`hover:bg-indigo-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
              data-testid={`product-row-${index}`}
            >
              <td className="px-1.5 py-1.5">
                <a 
                  href={product.website?.startsWith('http') ? product.website : `https://${product.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium flex items-center gap-1 text-xs"
                  title={product.website}
                  data-testid={`product-url-${index}`}
                >
                  <span className="truncate max-w-[120px] sm:max-w-[140px] 2xl:max-w-[190px]">
                    {product.website?.replace(/^https?:\/\//, '').replace(/\/$/, '') || '-'}
                  </span>
                  <ExternalLink className="h-3 w-3 opacity-60 shrink-0" />
                </a>
              </td>
              <td className="px-1.5 py-1.5 hidden sm:table-cell">
                {getCountryFlag(product.country) ? (
                  <img 
                    src={getCountryFlag(product.country)} 
                    alt={product.country}
                    title={product.country}
                    className="h-3 w-5 object-cover rounded-sm"
                  />
                ) : (
                  <span className="text-xs text-slate-400">{product.country?.substring(0,3) || '-'}</span>
                )}
              </td>
              <td className="px-1.5 py-1.5">
                <span className="font-bold text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded text-xs">€{product.pvp || product.price || 0}</span>
              </td>
              <td className="px-1.5 py-1.5 hidden md:table-cell">
                {product.premium_sale_price && product.premium_sale_price > 0 ? (
                  <span className="font-bold text-amber-600 bg-amber-50 px-1 py-0.5 rounded text-xs">€{product.premium_sale_price}</span>
                ) : (
                  <span className="text-[10px] font-medium text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded">
                    {language === 'es' ? 'No acepta' : "Doesn't accept"}
                  </span>
                )}
              </td>
              <td className="px-1 py-1.5 hidden min-[1680px]:table-cell">
                {product.allows_insertion && product.pvp_insertion > 0 ? (
                  <span className="font-bold text-indigo-700 bg-indigo-50 px-1 py-0.5 rounded text-[11px]">€{product.pvp_insertion}</span>
                ) : <span className="text-slate-300 text-[10px]">—</span>}
              </td>
              <td className="px-1 py-1.5 hidden min-[1680px]:table-cell">
                {product.allows_insertion && product.pvp_insertion_sensitive > 0 ? (
                  <span className="font-bold text-amber-600 bg-amber-50 px-1 py-0.5 rounded text-[11px]">€{product.pvp_insertion_sensitive}</span>
                ) : <span className="text-slate-300 text-[10px]">—</span>}
              </td>
              <td className="px-1 py-1.5 hidden min-[1680px]:table-cell">
                {product.allows_mention && product.pvp_mention > 0 ? (
                  <span className="font-bold text-rose-700 bg-rose-50 px-1 py-0.5 rounded text-[11px]">€{product.pvp_mention}</span>
                ) : <span className="text-slate-300 text-[10px]">—</span>}
              </td>
              <td className="px-1 py-1.5 hidden min-[1680px]:table-cell">
                {product.allows_mention && product.pvp_mention_sensitive > 0 ? (
                  <span className="font-bold text-amber-600 bg-amber-50 px-1 py-0.5 rounded text-[11px]">€{product.pvp_mention_sensitive}</span>
                ) : <span className="text-slate-300 text-[10px]">—</span>}
              </td>
              <td className="px-1.5 py-1.5">
                <span className="font-bold text-violet-600 bg-violet-50 px-1 py-0.5 rounded text-xs">{product.dr || '-'}</span>
              </td>
              <td className="px-1.5 py-1.5">
                <span className="font-medium text-sky-600 text-xs">{(product.traffic || product.organic_traffic || 0).toLocaleString()}</span>
              </td>
              <td className="px-1.5 py-1.5 hidden lg:table-cell" data-testid={`product-tf-${index}`}>
                {seoVal(product, 'tf') !== null ? <span className="font-semibold text-orange-600 text-xs">{seoVal(product, 'tf')}</span> : <Dash />}
              </td>
              <td className="px-1.5 py-1.5 hidden lg:table-cell" data-testid={`product-rd-${index}`}>
                {seoVal(product, 'rd') !== null ? <span className="font-semibold text-rose-600 text-xs">{seoVal(product, 'rd').toLocaleString()}</span> : <Dash />}
              </td>
              <td className="px-1.5 py-1.5 hidden lg:table-cell" data-testid={`product-kw-${index}`}>
                {seoVal(product, 'keywords') !== null ? <span className="font-semibold text-red-600 text-xs">{seoVal(product, 'keywords').toLocaleString()}</span> : <Dash />}
              </td>
              <td className="px-1.5 py-1.5 hidden lg:table-cell text-center" data-testid={`product-gn-${index}`}>
                {seoVal(product, 'gg_news') ? <Newspaper className="h-4 w-4 text-rose-500 inline" title="Google News" /> : <Dash />}
              </td>
              <td className="px-1.5 py-1.5 hidden lg:table-cell">
                {product.site_type ? (
                  <Badge className={`text-[10px] px-1 py-0 ${product.site_type?.toLowerCase().includes('periodico') ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'}`}>
                    {product.site_type}
                  </Badge>
                ) : <span className="text-slate-300">-</span>}
              </td>
              <td className="px-1.5 py-1.5 hidden xl:table-cell">
                <div className="flex flex-wrap gap-0.5">
                  {getLinkTypeBadges(product.link_type).slice(0, 3).map((badge, idx) => (
                    <Badge key={idx} className={`text-[10px] px-1 py-0 ${badge.color}`}>
                      {badge.label}
                    </Badge>
                  ))}
                  {getLinkTypeBadges(product.link_type).length === 0 && <span className="text-slate-300">—</span>}
                </div>
              </td>
              <td className="px-1.5 py-1.5 hidden xl:table-cell" data-testid={`product-duration-${index}`}>
                {product.link_duration ? (
                  <Badge className={`text-[10px] px-1.5 py-0 ${product.link_duration.toLowerCase() === 'permanente' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                    {product.link_duration.toLowerCase() === 'permanente' && language !== 'es' ? 'Permanent' : product.link_duration}
                  </Badge>
                ) : <Dash />}
              </td>
              <td className="px-1.5 py-1.5 text-center hidden xl:table-cell" data-testid={`product-sens-${index}`}>
                {SENSITIVE_KEYS.some((k) => product[k]) ? (
                  <Badge className="bg-rose-100 text-rose-700 text-[10px] rounded-full">{language === 'es' ? 'Sí' : 'Yes'}</Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-500 text-[10px] rounded-full">No</Badge>
                )}
              </td>
              <td className="px-1.5 py-1.5 text-center hidden xl:table-cell" data-testid={`product-tag-${index}`}>
                {['SI', 'SÍ', 'YES'].includes(product.ad_label?.toUpperCase()) ? (
                  <Badge className="bg-amber-100 text-amber-700 text-[10px] rounded-full">{language === 'es' ? 'Sí' : 'Yes'}</Badge>
                ) : (
                  <Badge className="bg-emerald-100 text-emerald-700 text-[10px] rounded-full">No</Badge>
                )}
              </td>
              <td className="px-1.5 py-1.5 text-center">
                <ExampleLinkIcon
                  url={product.publication_example_url}
                  testId={`product-example-${index}`}
                  label={language === 'es' ? 'Ver ejemplo de publicación' : 'View publication example'}
                  emptyLabel={language === 'es' ? 'Sin ejemplo de publicación' : 'No publication example'}
                />
              </td>
              <td className="px-1.5 py-1.5 text-center sticky right-0 bg-white/95">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onProductClick(product)}
                  className="h-6 w-6 p-0 rounded-full bg-indigo-100 text-indigo-600 hover:bg-indigo-200"
                  data-testid={`product-info-${index}`}
                >
                  <Info className="h-3 w-3" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </TooltipProvider>
    </div>
  );
};

export default ProductTable;
